import './select.css';

import _ from 'lodash';
import { Portal } from 'react-portal';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { usePopper } from 'react-popper';

import { faTimes, faAngleDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { focusNextTabable } from '../util/inputUtils';
import useDeepCompareEffect from '../hooks/useDeepCompareEffect';
import { stringsStartsWithIgnoreCase } from '../util';

function FilterableSelect2({
  selectId = undefined,
  items = [],
  onSelect = () => {},
  initialValue = undefined,
  placeholder = undefined,
  getId,
  getLabel,
  disabled,
  selectWidth = undefined,
  onTextChange,
  maxLength = undefined,
  dataType = 'text',
  clearable = false,
  hideClear = false,
  onBlur = undefined,
  name = undefined,
  fontColorItem = undefined,
}: {
  selectId?: string|undefined,
  items?: Array<any>
  onSelect: (x:any) => void,
  initialValue?: string|undefined,
  placeholder?: string|undefined,
  getId: (x:any, i:number) => string,
  getLabel: (x:any, i:number) => string,
  disabled: boolean,
  selectWidth?: string|undefined,
  onTextChange: (x:string) => void,
  maxLength?: number|undefined,
  dataType?: 'text' | 'number',
  clearable?: boolean,
  hideClear?: boolean,
  onBlur?: (() => void) | undefined,
  name?:string|undefined,
  fontColorItem?: any,
}) {
  const dark = false;

  const [selectedItem, _setSelectedItem] = useState(-1);
  const [open, _setOpen] = useState(false);
  const openRef = useRef(false);

  const [filteredItems, _setFilteredItems] = useState<Array<any>>([...items]);
  const [stringValue, _setStringValue] = useState('');
  const stringValueRef = useRef('');
  const skipStringValueCheckRef = useRef(false);

  const itemsRef = useRef(items);

  
  const filteredItemsRef = useRef<Array<any>>([...items]);

  function setFilteredItems(fi: Array<any>) {
    filteredItemsRef.current = fi;
    _setFilteredItems(fi);
  }



  const [referenceElement, _setReferenceElement] = useState<HTMLInputElement|null>(null);
  const [popperElement, _setPopperElement] = useState<HTMLDivElement|null>(null);
  const [, _setArrowElement] = useState<HTMLDivElement|null>(null);
  
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'bottom-start',
    strategy: 'fixed'
  });

  // const inputIdRef = useRef(_.uniqueId('ifms-select-'));
  const inputIdRef = useRef(selectId || _.uniqueId('ifms-select-'));

  const containerIdRef = useRef(_.uniqueId('ifms-select-container-'));

  const popperRef = useRef<HTMLDivElement|null>(null);
  const referenceRef = useRef<HTMLInputElement|null>(null);
  const arrowElementRef = useRef<HTMLDivElement|null>(null);
  const fieldClickRef = useRef(false);
  
  function setOpen(o:boolean) {
    openRef.current = o;
    _setOpen(o);
  }

  function setReferenceElement(e:HTMLInputElement|null) {
    referenceRef.current = e;
    _setReferenceElement(e);
  }

  function setPopperElement(e:HTMLDivElement|null) {
    popperRef.current = e;
    _setPopperElement(e);
  }

  function setArrowElement(e:HTMLDivElement|null) {
    arrowElementRef.current = e;
    _setArrowElement(e);
  }

  const selectedItemRef = useRef(
    (initialValue !== undefined && initialValue !== null)
      ? items.findIndex(x => x.value === initialValue)
      : -1
  );

  useEffect(() => {
      document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredItemsRef.current]
  );

  useEffect(() => {
    if (open) {
      scrollIntoView(selectedItemRef.current);
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [open]
);

  useEffect(() => {
      if (referenceElement) {
        referenceElement.value = initialValue ?? '';
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialValue]
  );

  useDeepCompareEffect(() => {
      itemsRef.current = items;

      if (skipStringValueCheckRef.current) {
        skipStringValueCheckRef.current = false;
        return;
      }
      const fi = initialValue
        ? items.filter(x => {
          return stringsStartsWithIgnoreCase(x, initialValue as string);
        })
        : [...items];

      setFilteredItems(fi);
      setSelectedItem(fi.length > 0 && initialValue ? 0 : -1);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialValue, items]
  );

  function openWithoutFilter(targetId?: string) {
    if (targetId && referenceRef.current?.id !== targetId) return;

    setOpen(!openRef.current);

    if (openRef.current) {
      // Show full list without filters
      let newSelectedItem = selectedItemRef.current;
      if (newSelectedItem >= 0) {
        const newFilteredItemValue = filteredItems[newSelectedItem];
        newSelectedItem = items.findIndex(x => x === newFilteredItemValue);
      }
      setFilteredItems([...items]);
      setSelectedItem(newSelectedItem);
    }

    if (referenceElement) {
      referenceElement.focus();
    }
  }



  function handleClickOutside(e:MouseEvent) {
    if (!popperRef || !referenceRef || !popperRef.current || !referenceRef.current || !arrowElementRef.current) {
      return;
    }

    const target = e.target as Element;

    if (!popperRef.current.contains(target) && !referenceRef.current.contains(target) && !arrowElementRef.current.contains(target)) {
      processBlur();
    }
  }

  function blurInput() {
    if (referenceElement) {
      referenceElement.blur();
    }
  }

  function clear() {
    setOpen(false);
    blurInput();
    onSelect(null);
    if (referenceElement) {
      referenceElement.value = '';
    }
  }

  function isScrolledIntoView(container:HTMLElement, row:HTMLElement) {
    const trect = container.getBoundingClientRect();
    const rect = row.getBoundingClientRect();

    const viewStart = trect.top;
    const viewEnd = trect.top + trect.height;

    return rect.top >= viewStart && (rect.top + rect.height) <= viewEnd;
  }

  function setSelectedItem(index: number) {
    scrollIntoView(index);
    selectedItemRef.current = index;
    _setSelectedItem(index);
  }

  function scrollIntoView(index: number) {
    const container = document.getElementById(containerIdRef.current);
    const row = document.getElementById(`${containerIdRef.current}-item-${index}`);

    if (!row || !container) {
      return;
    }

    if (index < 0) {
      container.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
      return;
    }

    if (isScrolledIntoView(container, row)) {
      return;
    }

    const newPos = row.offsetTop;

    container.scrollTo({
      top: newPos >= 0 ? newPos : 0,
      left: 0,
      behavior: 'smooth'
    });
  }

  function preselect(text:string|null) {
    if (!text) {
      setSelectedItem(-1);
      return;
    }
    setFilteredItems([...items]);
    const match = items.findIndex((x, i) => getLabel(x, i).toLowerCase().startsWith(text.toLowerCase()));
    setSelectedItem(match);
  }

  function show() {
    if (referenceElement && referenceElement.value) {
      preselect(referenceElement.value);
    }
    setOpen(true);
  }

  function hide() {
    setOpen(false);
  }

  function processBlur() {
    if (selectedItem >= 0 && selectedItem < items.length) {
      onListItemSelected(items[selectedItem]);
      return;
    }
    hide();
    fieldClickRef.current = false;
  }

  function onListItemSelected(item:any) {
    onSelect(item);
    hide();

    // setTimeout(() => {
    //   if (referenceElement) {
    //     referenceElement.focus();
    //   }
    // }, 10);
    //focus next tabable field after select
    setTimeout(() => {
      if (referenceRef.current)
        focusNextTabable(referenceRef.current)
    }, 10)
  }

  function handleTextChange(e:React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value;

    preselect(val);

    if (!open) {
      show();
    }
    onTextChange(val);
  }

  function processSpecialKeys(e:React.KeyboardEvent) {
    if (!e || !e.keyCode) {
      return;
    }

    switch(e.keyCode) {
      case 40: // Arrow down
        if (selectedItem < items.length - 1) {
          setSelectedItem(selectedItem + 1);
        }
        break;
      case 38: // Arrow up
        if (selectedItem > 0) {
          setSelectedItem(selectedItem -1);
        }
        break;
      case 9: // Tab
      if (openRef.current) {
        if (selectedItemRef.current >= 0 && selectedItemRef.current < items.length) {
          onListItemSelected(items[selectedItemRef.current]);
          e.stopPropagation();
          e.preventDefault();
        }
        //blurInput();
        hide();
      }
        break;
      case 13: // Enter
        if (!openRef.current) {
          show();
          return;
        }
      
        if (selectedItem >= 0 && selectedItem < items.length) {
          if(selectedItem < filteredItems.length){
            onListItemSelected(filteredItems[selectedItem]);
          }else {
            onListItemSelected(items[selectedItem]);
          }
        } else {
          hide();
        }
        break;
      default:
    }
  }

  if (disabled) {
    const cssClass = dark ? 'e-ifms-input-dark e-outline ifms-tabable' : 'e-ifms-input e-outline ifms-tabable';

    return (
      <input
        type="text"
        name={name}
        className={cssClass}
        value={initialValue}
        onChange={() => {}}
        disabled
        placeholder={placeholder}
        autoComplete="off"
        list="autocompleteOff"
      />
    );
  }

  const hasSelectedValue = referenceRef.current && referenceRef.current.value;
  const selectedBaseClass = hasSelectedValue ? 'ifms-select-with-value' : '';

  const baseClass = disabled ? `e-ifms-numeric-no-arrows ifms-select-no-caret ${selectedBaseClass}` : `e-ifms-numeric-no-arrows ifms-select ${selectedBaseClass}`;

  let cssClass = dark ? `${baseClass} e-ifms-input-dark e-outline ifms-tabable` : `${baseClass} e-ifms-input e-outline ifms-tabable`;
  if (!disabled) {
    cssClass = `${cssClass} ifms-tabable-active`;
  }


  const inputRect = referenceElement ? referenceElement.getBoundingClientRect() : null;

  const appliedPopperStyles = {
    ...styles.popper,
    width: selectWidth ? selectWidth : (`${inputRect ? inputRect.width : 100}px`),
  };

  return (
    <div className="ifms-select-container">
      <input
        id ={inputIdRef.current}
        name={name}
        style={{paddingRight: '15px'}}
        ref={setReferenceElement}
        type={dataType}
        className={cssClass}
        defaultValue={initialValue}
        placeholder={placeholder}
        onChange={handleTextChange}
        maxLength={maxLength}
        // onBlur={processBlur}
        onKeyDown={processSpecialKeys}
        autoComplete={inputIdRef.current}
        data-tooltip="-from-value"
        
        data-tooltip-onlywhencutoff
        onClick={(e) => {
          if (!disabled && !fieldClickRef.current) {
            (e.target as HTMLInputElement).select();
            fieldClickRef.current = true;
          }
        }}
        onBlur={onBlur}
      />
      {(clearable && !hideClear) &&
        <div
          id={`${name}-select-drop-icon`}  className={disabled ? 'ifms-select-drop-icon-container ifms-select-drop-icon-container-disabled' : 'ifms-select-drop-icon-container ifms-select-drop-icon-container-enabled'}
        >
          <FontAwesomeIcon icon={faTimes} className="ifms-filterable-select-clear-icon" onClick={clear} />
        </div>
      }
      {!clearable &&
        <div
        id={`${name}-select-drop-icon`} className={disabled ? 'ifms-select-drop-icon-container ifms-select-drop-icon-container-disabled' : 'ifms-select-drop-icon-container ifms-select-drop-icon-container-enabled'}
          onClick={() => {
            if (disabled) {
              return;
            }
            // setOpen(!openRef.current);
            // if (openRef.current) {
            //   if (referenceElement && referenceElement.value) {
            //     preselect(referenceElement.value);
            //   }
            // }
            // if (referenceElement) {
            //   referenceElement.focus();
            // }
            openWithoutFilter();
          }}
          ref={setArrowElement}
        >
          <FontAwesomeIcon icon={faAngleDown} className="ifms-filterable-select-drop-icon" />
        </div>
      }
    
      {open &&
        <Portal node={document && document.getElementById('ifms-app-poppers')}>
          <div id={(name?`${name}-select-box`:containerIdRef.current)} ref={setPopperElement} className="ifms-select-box" style={appliedPopperStyles} {...attributes.popper}>
            {filteredItems.map((x, i) => (
              <div
                id={(name?`${name}-select-box-item-${i}`:`${containerIdRef.current}-item-${i}`)}
                key={getId(x, i)}
                className={`${selectedItem === i ? 'ifms-select-item-selected ': 'ifms-select-item'}`}
                onClick={() => onListItemSelected(x)}
                style={{color: (fontColorItem && fontColorItem[i].title === getLabel(x,i) ? fontColorItem[i].color : 'black')}}
              >
                {getLabel(x, i)}
              </div>
            ))}
          </div>
        </Portal>
      }
    </div>
  );
}

FilterableSelect2.defaultProps = {
  onSelect: () => {},
  disabled: false,
  dark: false,
  placeholder: '',
  onTextChange: () => {},
  dataType: 'text',
  clearable: false,
  hideClear: false,
};

FilterableSelect2.propTypes = {
  selectId: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  getId: PropTypes.func.isRequired,
  getLabel: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
  disabled: PropTypes.bool.isRequired,
  dark: PropTypes.bool.isRequired,
  selectWidth: PropTypes.string,
  items: PropTypes.array,
  initialValue: PropTypes.string,
  onTextChange: PropTypes.func.isRequired,
  maxLength: PropTypes.number,
  dataType: PropTypes.oneOf(['text', 'number']).isRequired,
  clearable: PropTypes.bool.isRequired,
  hideClear: PropTypes.bool.isRequired,
};

export default React.memo(FilterableSelect2);
